import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import FormModal from "flarum/common/components/FormModal";
import Avatar from "flarum/common/components/Avatar";
import Button from "flarum/common/components/Button";
import LoadingIndicator from "flarum/common/components/LoadingIndicator";
import username from "flarum/common/helpers/username";
import classList from "flarum/common/utils/classList";
import { isOnline } from "../utils/presence";

/** What the endpoint accepts in one call; mirrors ChannelResource::addMembers. */
const MAX_PER_REQUEST = 50;

/** How many recently active people are offered before anything is typed. */
const SUGGESTIONS = 8;
/**
 * Picks people to invite into a channel.
 *
 * A token field: the chosen people sit inside the search box as chips, so the
 * question "who am I about to invite" is answered where the typing happens.
 * Before anything is typed the box offers the people most recently seen on the
 * forum, so the dialog never opens onto an empty grey square; typing narrows
 * that to a search. People who are already in, or already asked, still appear
 * in the results, marked and unselectable, so their absence from the offer is
 * explained rather than silent.
 *
 * The keyboard works the way a token field is expected to: arrows move along
 * the list, Enter picks, Backspace on an empty box takes the last chip back,
 * Escape clears the query.
 */
export default class AddMembersModal extends FormModal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "query", "");
    _defineProperty(this, "results", []);
    _defineProperty(this, "suggestions", []);
    _defineProperty(this, "selected", []);
    _defineProperty(this, "searching", false);
    _defineProperty(this, "loadingSuggestions", true);
    _defineProperty(this, "highlighted", 0);
    _defineProperty(this, "timer", null);
    /**
     * Guards against an earlier search resolving after a later one.
     *
     * Typing "ram" issues a request for "ra" and one for "ram"; without this the
     * slower of the two wins and the list contradicts the field.
     */
    _defineProperty(this, "sequence", 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    void this.loadSuggestions();
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this.timer !== null) window.clearTimeout(this.timer);
  }
  className() {
    return "ChatModal ChatAddMembers Modal--medium";
  }
  title() {
    return app.translator.trans("ramon-chat.forum.add_members.title");
  }
  content() {
    return m("div", {
      className: "Modal-body ChatAddMembers-body"
    }, m("p", {
      className: "ChatAddMembers-intro"
    }, app.translator.trans("ramon-chat.forum.add_members.intro", {
      channel: m("strong", null, this.attrs.channel.displayName())
    })), m("div", {
      className: "ChatAddMembers-field",
      onclick: () => this.focusInput()
    }, m("i", {
      className: "ChatAddMembers-field-icon fas fa-magnifying-glass",
      "aria-hidden": "true"
    }), this.selected.map(user => m("button", {
      type: "button",
      key: user.id(),
      className: "ChatAddMembers-chip",
      disabled: this.loading,
      title: app.translator.trans("ramon-chat.forum.add_members.deselect", {
        username: username(user)
      }, true),
      onclick: e => {
        e.stopPropagation();
        this.toggle(user);
      }
    }, m(Avatar, {
      user: user,
      className: "Avatar"
    }), m("span", null, user.displayName()), m("i", {
      className: "fas fa-xmark",
      "aria-hidden": "true"
    }))), m("input", {
      className: "ChatAddMembers-input",
      type: "text",
      autocomplete: "off",
      placeholder: this.selected.length === 0 ? app.translator.trans("ramon-chat.forum.add_members.search_placeholder", {}, true) : "",
      value: this.query,
      disabled: this.loading,
      oninput: e => this.search(e.target.value),
      onkeydown: e => this.onKey(e),
      oncreate: vnode => vnode.dom.focus()
    }), this.searching ? m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }) : null), this.list());
  }

  /**
   * The offer: suggestions until two letters are typed, then the search.
   */
  list() {
    const typed = this.query.trim().length;
    if (typed < 2) {
      if (this.loadingSuggestions) {
        return m("div", {
          className: "ChatAddMembers-results ChatAddMembers-results--empty"
        }, m(LoadingIndicator, {
          display: "inline",
          size: "small"
        }));
      }
      const candidates = this.suggestions.filter(user => this.statusOf(user) === null);
      if (candidates.length === 0) {
        return m("p", {
          className: "ChatAddMembers-hint"
        }, app.translator.trans("ramon-chat.forum.add_members.hint"));
      }
      return m("div", {
        className: "ChatAddMembers-results"
      }, m("div", {
        className: "ChatAddMembers-heading"
      }, app.translator.trans("ramon-chat.forum.add_members.suggested")), candidates.map((user, index) => this.row(user, index)));
    }
    if (this.results.length === 0) {
      return m("p", {
        className: "ChatAddMembers-hint"
      }, this.searching ? app.translator.trans("ramon-chat.forum.add_members.searching") : app.translator.trans("ramon-chat.forum.add_members.no_results", {
        query: this.query.trim()
      }));
    }
    return m("div", {
      className: "ChatAddMembers-results",
      role: "listbox"
    }, this.rows(this.results));
  }

  /**
   * Rows for a list that mixes people who can be picked with people who
   * cannot. The highlight counts only the former, the way the arrow keys do,
   * so a marked row in the middle of the results does not shift it.
   */
  rows(users) {
    let offeredIndex = 0;
    return users.map(user => this.row(user, this.statusOf(user) === null ? offeredIndex++ : -1));
  }
  row(user, index) {
    const status = this.statusOf(user);
    const picked = this.isSelected(user);
    const offered = status === null;
    return m("button", {
      type: "button",
      key: user.id(),
      className: classList("ChatAddMembers-result", {
        "ChatAddMembers-result--selected": picked,
        "ChatAddMembers-result--highlighted": offered && index === this.highlighted,
        "ChatAddMembers-result--taken": !offered
      }),
      role: "option",
      "aria-selected": picked,
      disabled: this.loading || !offered,
      onmouseenter: () => {
        this.highlighted = index;
      },
      onclick: () => this.toggle(user)
    }, m("span", {
      className: classList("ChatAddMembers-result-avatar", {
        "ChatAddMembers-result-avatar--online": isOnline(user)
      })
    }, m(Avatar, {
      user: user,
      className: "Avatar"
    })), m("span", {
      className: "ChatAddMembers-result-text"
    }, m("span", {
      className: "ChatAddMembers-result-name"
    }, user.displayName()), user.displayName() !== user.username() ? m("span", {
      className: "ChatAddMembers-result-handle"
    }, "@", user.username()) : null), status ? m("span", {
      className: "ChatAddMembers-result-status"
    }, app.translator.trans("ramon-chat.forum.add_members.".concat(status, "_badge"))) : m("span", {
      className: "ChatAddMembers-result-mark",
      "aria-hidden": "true"
    }, m("i", {
      className: picked ? "fas fa-check" : "fas fa-plus"
    })));
  }
  footer() {
    const count = this.selected.length;
    const tooMany = count > MAX_PER_REQUEST;
    return m("div", {
      className: "Modal-footer ChatAddMembers-footer"
    }, m("span", {
      className: "ChatAddMembers-footer-count"
    }, tooMany ? app.translator.trans("ramon-chat.api.members_too_many", {
      max: MAX_PER_REQUEST
    }) : count > 0 ? app.translator.trans("ramon-chat.forum.add_members.selected_count", {
      count
    }) : null), m(Button, {
      className: "Button Button--link",
      type: "button",
      disabled: this.loading,
      onclick: () => this.hide()
    }, app.translator.trans("ramon-chat.forum.new_channel.cancel")), m(Button, {
      className: "Button Button--primary",
      type: "submit",
      icon: "fas fa-paper-plane",
      loading: this.loading,
      disabled: this.loading || count === 0 || tooMany
    }, count === 0 ? app.translator.trans("ramon-chat.forum.add_members.submit") : app.translator.trans("ramon-chat.forum.add_members.submit_count", {
      count
    })));
  }
  inner() {
    return m('[', null, super.inner(), this.footer());
  }

  // ── Behaviour ──────────────────────────────────────────────────────────────

  /**
   * Why a person cannot be picked, or null when they can.
   */
  statusOf(user) {
    var _app$session$user;
    const id = user.id();
    if (id === ((_app$session$user = app.session.user) == null ? void 0 : _app$session$user.id())) return "you";
    if (this.attrs.members.some(member => member.id() === id)) return "member";
    if (this.attrs.invited.some(pending => pending.id() === id)) {
      return "invited";
    }
    return null;
  }
  isSelected(user) {
    return this.selected.some(picked => picked.id() === user.id());
  }
  toggle(user) {
    if (this.statusOf(user) !== null) return;
    this.selected = this.isSelected(user) ? this.selected.filter(picked => picked.id() !== user.id()) : [...this.selected, user];

    // Picking someone from a search clears it, the way a token field does:
    // the chip is the record of the choice, and the next name starts fresh.
    if (this.query !== "") {
      this.query = "";
      this.results = [];
      this.searching = false;
      this.sequence++;
    }
    this.highlighted = 0;
    this.focusInput();
  }
  offered() {
    const typed = this.query.trim().length;
    const pool = typed < 2 ? this.suggestions : this.results;
    return pool.filter(user => this.statusOf(user) === null);
  }
  onKey(e) {
    const offered = this.offered();
    if (e.key === "ArrowDown" && offered.length > 0) {
      e.preventDefault();
      this.highlighted = (this.highlighted + 1) % offered.length;
    } else if (e.key === "ArrowUp" && offered.length > 0) {
      e.preventDefault();
      this.highlighted = (this.highlighted - 1 + offered.length) % offered.length;
    } else if (e.key === "Enter") {
      // Enter picks; it must not submit the form behind the field, which is
      // what a bare input inside a form does.
      e.preventDefault();
      const pick = offered[this.highlighted];
      if (pick) this.toggle(pick);
    } else if (e.key === "Backspace" && this.query === "") {
      const last = this.selected[this.selected.length - 1];
      if (last) this.toggle(last);
    } else if (e.key === "Escape" && this.query !== "") {
      e.stopPropagation();
      this.search("");
    }
  }
  focusInput() {
    var _this$element;
    const input = (_this$element = this.element) == null ? void 0 : _this$element.querySelector(".ChatAddMembers-input");
    input == null || input.focus();
  }

  /**
   * A handful of people offered before any typing.
   *
   * The most recently seen when the actor may sort by that (core gates the
   * `lastSeenAt` sort behind `user.viewLastSeenAt`, which ordinary members
   * usually lack and which the API answers with a 400), the most active
   * posters otherwise. Fetched once per opening; the list is small and the
   * point is only to give the dialog something to open onto.
   */
  async loadSuggestions() {
    var _app$session$user2;
    const limit = SUGGESTIONS + 1 + this.attrs.members.length;
    // Administrators hold every permission; anyone else who happens to hold
    // this one is caught by the fallback rather than by a second request
    // for everyone.
    const orders = (_app$session$user2 = app.session.user) != null && _app$session$user2.isAdmin() ? ["-lastSeenAt", "-commentCount"] : ["-commentCount", "-lastSeenAt"];
    try {
      let results = [];
      for (const sort of orders) {
        try {
          const found = await app.store.find("users", {
            sort,
            page: {
              limit
            }
          });
          results = Array.isArray(found) ? found : [];
          break;
        } catch (_unused) {
          // A sort this actor may not use; the next one is open to everyone.
        }
      }
      this.suggestions = results.slice(0, limit);
    } finally {
      this.loadingSuggestions = false;
      m.redraw();
    }
  }

  /** Debounced so typing does not issue a request per keystroke. */
  search(value) {
    this.query = value;
    this.highlighted = 0;
    if (this.timer !== null) window.clearTimeout(this.timer);
    if (value.trim().length < 2) {
      this.results = [];
      this.searching = false;
      this.sequence++;
      return;
    }
    this.searching = true;
    const mine = ++this.sequence;
    this.timer = window.setTimeout(() => {
      app.store.find("users", {
        filter: {
          q: value.trim()
        },
        page: {
          limit: 10
        }
      }).then(results => {
        if (mine !== this.sequence) return;
        this.results = Array.isArray(results) ? results : [];
        this.searching = false;
        m.redraw();
      }).catch(() => {
        if (mine !== this.sequence) return;
        this.results = [];
        this.searching = false;
        app.alerts.show({
          type: "error"
        }, app.translator.trans("ramon-chat.forum.add_members.search_failed"));
        m.redraw();
      });
    }, 250);
  }
  onsubmit(e) {
    e.preventDefault();
    if (this.loading || this.selected.length === 0) return;
    this.loading = true;
    m.redraw();
    const chosen = this.selected;
    app.request({
      method: "POST",
      url: "".concat(app.forum.attribute("apiUrl"), "/chat-channels/").concat(this.attrs.channel.id(), "/members"),
      // One request for the whole selection: the endpoint takes a list, and
      // inviting five people used to be five requests, five notifications'
      // worth of round trips and five chances to half-fail.
      body: {
        data: {
          attributes: {
            userIds: chosen.map(u => Number(u.id()))
          }
        }
      }
    }).then(payload => {
      if (payload != null && payload.data) app.store.pushPayload(payload);
      this.hide();
      app.alerts.show({
        type: "success"
      }, app.translator.trans("ramon-chat.forum.add_members.added", {
        count: chosen.length,
        names: chosen.map(user => user.displayName()).join(", ")
      }));
      this.attrs.onAdded(chosen);
    }).catch(error => {
      this.loading = false;
      if (error != null && error.alert) {
        this.onerror(error);
      } else {
        var _error$response$error, _error$response;
        app.alerts.show({
          type: "error"
        }, (_error$response$error = error == null || (_error$response = error.response) == null || (_error$response = _error$response.errors) == null || (_error$response = _error$response[0]) == null ? void 0 : _error$response.detail) != null ? _error$response$error : app.translator.trans("ramon-chat.forum.add_members.failed"));
        m.redraw();
      }
    });
  }
}
flarum.reg.add('ramon-chat', 'forum/components/AddMembersModal', AddMembersModal);